import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { syncTaskTimer } from '../api/taskTimer';
import { getSessionEpoch, subscribeSessionRecovered } from '../lib/auth/session';
import { taskCreated } from '../lib/store/taskUpdatesReducer';
import { isOwnTaskOperation } from '../lib/store/taskOperations';
import { createTimerNotifier, publishTimerChange, subscribeTimerChanges } from '../lib/timerNotifications';

// Mounted once in App: individual task rows never own hub subscriptions.
export function useTimerSync(isAuth, timerHub, updateHub) {
    const dispatch = useDispatch();
    const revision = useSelector(state => state.taskUpdates);
    const changes = useSelector(state => state.stopWatchesData.changes);
    const entries = useSelector(state => state.stopWatchesData.stopWatchesData);
    const previousChange = useRef(changes);
    const previousRevision = useRef(revision);
    const previousSyncChange = useRef(changes);
    const revisionAlreadySynced = useRef(false);
    const ownEvent = useRef('');
    const connectedHub = useRef(timerHub);
    const notifier = useRef(null);
    const entriesRef = useRef(entries);
    entriesRef.current = entries;
    useEffect(() => {
        const force = previousRevision.current !== revision;
        const localChange = previousSyncChange.current !== changes;
        previousSyncChange.current = changes;
        previousRevision.current = revision;
        if (revisionAlreadySynced.current) { revisionAlreadySynced.current = false; return; }
        if (!isAuth || localChange) return;
        dispatch(syncTaskTimer({force})).catch(() => {});
    }, [dispatch, isAuth, revision, changes]);
    useEffect(() => {
        if (!isAuth) return;
        const owner = localStorage.getItem('userID');
        const epoch = getSessionEpoch();
        const reconcileView = () => {
            if (owner !== localStorage.getItem('userID') || epoch !== getSessionEpoch()) return;
            revisionAlreadySynced.current = true;
            dispatch(taskCreated());
            dispatch(syncTaskTimer({force: true})).catch(() => {});
        };
        const receive = (userId, taskId, startTime, active) => {
            if (userId !== localStorage.getItem('userID')) return;
            if (JSON.stringify([taskId, startTime, active]) === ownEvent.current) return;
            reconcileView();
        };
        timerHub?.on('Receive', receive);
        // The initial GET may have finished before this connection joined its group.
        if (timerHub && connectedHub.current !== timerHub) reconcileView();
        connectedHub.current = timerHub;
        let lastReturn = -Infinity;
        const onReturn = () => {
            if (document.visibilityState === 'hidden' || Date.now() - lastReturn < 1000) return;
            lastReturn = Date.now();
            notifier.current?.flush();
            reconcileView();
        };
        const offSession = subscribeSessionRecovered(reconcileView);
        const offTabs = subscribeTimerChanges(reconcileView);
        window.addEventListener('focus', onReturn);
        window.addEventListener('online', onReturn);
        document.addEventListener('visibilitychange', onReturn);
        return () => {
            timerHub?.off('Receive', receive);
            offSession(); offTabs();
            window.removeEventListener('focus', onReturn);
            window.removeEventListener('online', onReturn);
            document.removeEventListener('visibilitychange', onReturn);
        };
    }, [dispatch, isAuth, timerHub]);
    useEffect(() => {
        if (!isAuth) return;
        const receive = (userId, operationId) => {
            if (userId === localStorage.getItem('userID') && !isOwnTaskOperation(operationId)) {
                dispatch(syncTaskTimer({force: true})).catch(() => {});
            }
        };
        updateHub?.on('Receive', receive);
        return () => updateHub?.off('Receive', receive);
    }, [dispatch, isAuth, updateHub]);
    useEffect(() => {
        if (!isAuth) return;
        const sender = createTimerNotifier(timerHub, updateHub, signature => {ownEvent.current = signature;});
        notifier.current = sender;
        return () => { sender.stop(); if (notifier.current === sender) notifier.current = null; };
    }, [isAuth, timerHub, updateHub]);
    useEffect(() => {
        const localChange = previousChange.current !== changes;
        previousChange.current = changes;
        if (!isAuth) return;
        if (localChange && changes > 0) publishTimerChange();
        const current = entriesRef.current.find(t => t.isTimerActive) || entriesRef.current.slice(-1)[0];
        notifier.current?.announce(changes, current);
        notifier.current?.flush();
    }, [changes, revision, isAuth, timerHub, updateHub]);
}
